import React, { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FormType } from "../../constants/contextState";
import categoryData from "../../constants/many-manyCat.json";
import ModalControlRouterWrapper from "../../components/forms/campaignAuthorizationForm/modalControlRouterWrapper";
import CampaignStateManagerFlowForms from "../../components/forms/campaignAuthorizationForm/campaignStateManagerFlowForms.jsx";
import DatePicker from "../../components/datePicker/datePicker";
import PointsDashBoard from "../../components/rewardPoints/pointsDashBoard.jsx";
import { useAuth } from "../../components/context/authContext.jsx";
import { useNotifications } from "../../components/context/notificationContext.jsx";
import { getStorageKey } from "../../utils/localStorageStateUniqueKeyGenerator.js";
import { FORM_FLOW_CONTROL } from "../../constants/currentContextTypes.js";
import Television from "../../components/television/television.jsx";
import {
  GlassBtn,
  ImgTopBtn,
  SolidColorButton,
  SmallPlainInsetBtn,
} from "../../components/buttons";
import {
  BroadCastTowerIcon,
  ClipBoardCheckIcon,
  DesktopComputerIcon,
  CatagoriesIcon,
  CashRegisterIcon,
  MagnifyingGlassIcon,
  XIcon,
  CalendarIcon,
  EyeIcon,
  EyeSlashIcon,
  LeftArrow,
} from "../../components/icons";
import {
  apiFetchCurrentUserDetails,
} from "../../api/backend/user.js";
import {  apiFetchCampaigns,
} from "../../api/backend/campaign.js";
import CampaignMetricsCard from "../../components/campaignMetricsCard/campaignMetricsCard.jsx";
import ErrorMessageModal from "../../components/errorMessageModal/errorMessageModal.jsx";
import { setCookie, clearCookie } from "../../utils/cookies.js";
import {
  apiDeleteSourcesCamapainAndRelatedRecords,
  apiToggleCampaignAutoPay,
} from "../../api/backend/campaign.js";
import Loader from "../../components/loader/loader.jsx";
import useDebounce from "../../components/hooks/useDebounce.js";
import { autopayErrorMessages } from "../../constants/errorMessages";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions.js";
import "./adsManagerDashboard.scss";

const CurrentUserAdsManagerDashboard = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState([]);
  const navigate = useNavigate();
  const [lastStartedAt, setStartedCreatedAt] = useState(null); // Tracks the last createdAt value
  const [hasMore, setHasMore] = useState(true); // Whether there are more campaigns to fetch
  const {
    currentMemberId,
    currentMemberData,
    setCurrentMemberData,
    verifyNeeded,
    setVerifyNeeded,
    currentContextType,
    setCurrentContextType,
    setCurrentFormStep,
    handleLogout,
  } = useAuth();
  const { notificationsCountFetchedFromMiddleware } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTermValue, setSearchTermValue] = useState("");
  const debouncedSearchTerm = useDebounce(searchTermValue, 300); // this is a hook imported used for filtering
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [isCampaignSearchFocused, setCampaignSearchFocused] = useState(false);
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [currentForm, setCurrentForm] = useState(FormType.CampaignAdModal);
  const [formType, setFormType] = useState(FormType.CampaignAdModal);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [categories, setCategories] = useState(categoryData.parents); // Assuming your parent categories are in the "parents" array
  const [modalTitle, setModalTitle] = useState("");
  const [modalIcon, setModalIcon] = useState(null);

  // Fetching and setting initial values from local storage for the date filters
  const [startDateValue, setStartDateValue] = useState(""); // Initialize as an empty string
  const [endDateValue, setEndDateValue] = useState(""); // Initialize as an empty string
  const [showDates, setShowDates] = useState(false); // parent is managing the filters so if it is false then you will see the button that says dates once you press it then you will see the fields

  const [dateOrderError, setDateOrderError] = useState(false);

  const [subCatMenuData, setSubCatMenuData] = useState({
    subCatMenuOpen: false,
    data: { subcategories: [] },
    categoryName: "",
    categoryIcon: "",
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await apiFetchCurrentUserDetails(currentMemberId); // Fetch user details from API

        if (response && response.data) {
          setCurrentMemberData((prevAttributes) => ({
            ...prevAttributes, // Preserve existing attributes
            pointsBalance: parseFloat(
              response.data.currentPointsBalance
            ).toFixed(2), // Format points balance to 2 decimals
            currentRewardLevel: response.data.currentRewardLevel, // Update reward level
          }));
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      } finally {
        setLoading(false); // Ensure loading stops in both success and error cases
      }
    };

    fetchUserDetails(); // Invoke the function to fetch user details
  }, [currentMemberId]); // Only run this effect when currentMemberId changes
  // Only run this effect when currentMemberId changes

  useEffect(() => {
    const storedData = localStorage.getItem("subCatMenuData");
    if (storedData) {
      setSubCatMenuData(JSON.parse(storedData));
    }
  }, []);

  useEffect(() => {
    setCategories(categoryData.parents);
  }, []);

  const searchValuesCollected = (event) => {
    setSearchTermValue(event.target.value);
  };

  // Recreation on Dependency Change:
  // The function is re-created only when one of
  // the dependencies changes. In your case, if
  // any of the functions (setCampaigns,
  // setCurrentForm, setFormType) or
  // currentContextType changes, a new instance of
  // handleSaveAd is generated with the updated values.
  // Otherwise, it reuses the same instance. The function would not recreate or it would memoize
  // unelss it is invoked
  // it will return the same function unless change happens or it is writtent to have values passed
  // and invoke it
  const handleSaveAd = useCallback(
    (updatedAd) => {
      const storedCampaignName =
        localStorage.getItem(
          getStorageKey("campaignName", currentContextType)
        ) || "";

      const campaignToSave = {
        ...updatedAd,
        campaignName: updatedAd.campaignName || storedCampaignName,
      };

      setCampaigns((prevCampaigns) => {
        if (!Array.isArray(prevCampaigns)) {
          console.error(
            "Expected campaigns to be an array but got:",
            prevCampaigns
          );
          return [campaignToSave];
        }

        // Here, the id that was stored in state (via setCampaign) is used to
        // determine if the campaign already exists in the campaign list, ensuring
        // that the user updates the correct campaign.
        const isExistingCampaign = prevCampaigns.some(
          (campaign) => campaign.id === campaignToSave.id
        );

        if (isExistingCampaign) {
          return prevCampaigns.map((campaign) =>
            campaign.id === campaignToSave.id
              ? { ...campaign, ...campaignToSave }
              : campaign
          );
        } else {
          return [...prevCampaigns, { id: Date.now(), ...campaignToSave }];
        }
      });

      setCurrentForm(FormType.CategoryNameFormatSearch);
      setFormType(FormType.CategoryNameFormatSearch);
    },
    [setCampaigns, setCurrentForm, setFormType, currentContextType]
  );

  const saveSetCampaignAmount = (paymentDetails) => {
    if (currentContextType === "createCampaign") {
      setCampaign(paymentDetails);
      setCurrentForm(FormType.Checkout);
      setFormType(FormType.Checkout);
    } else if (currentContextType === "updateCampaign") {
      setCampaign(paymentDetails);
    }
    // You can call these once outside the if/else block if they apply to both modes:
    setCurrentForm(FormType.Checkout);
    setFormType(FormType.Checkout);
  };

  const handleCategoryClick = (categoryName, categoryId) => {
    const category = categoryData.parents.find(
      (parent) => parent.id === categoryId
    );
    const subcategories = categoryData.childs.filter((child) =>
      child.parent_categories.includes(categoryId)
    );
    if (category) {
      localStorage.setItem(
        "subCatMenuData",
        JSON.stringify({
          idCategory: categoryId,
          subCatMenuOpen: true,
          data: { subcategories },
          categoryName: category.name,
          categoryIcon: category.iconPath,
        })
      );
      setSubCatMenuData({
        idCategory: categoryId,
        subCatMenuOpen: true,
        data: { subcategories },
        categoryName: category.name,
        categoryIcon: category.iconPath,
      });
      setCurrentForm(FormType.RelationalCategoryMenuPopup);
      setFormType(FormType.RelationalCategoryMenuPopup);
    }
  };

  const handleSubcategoryClick = (subcategoryId, subcategoryName) => {
    const key = getStorageKey("adCategoryPromo", currentContextType);
    // Store both subcategoryId and subcategoryName in local storage
    localStorage.setItem(
      key,
      JSON.stringify({ subcategoryId, subcategoryName }) // Do not overwrite this
    );

    setSubCatMenuData({ subCatMenuOpen: false, data: { subcategories: [] } });
    setCurrentForm(FormType.CampaignAmount);
    setFormType(FormType.CampaignAmount);
  };

  const handlePromoteAllClick = (categoryId, categoryName) => {
    // Log to trace categoryId and categoryName before saving to local storage
    console.log("categoryId:", categoryId, "categoryName:", categoryName);
    const key = getStorageKey("adCategoryPromo", currentContextType);
    // Store both categoryId and categoryName in local storage
    localStorage.setItem(
      key,
      JSON.stringify({
        relationalCategoryId: categoryId,
        relationalCategoryName: categoryName,
      })
    );
    setCurrentForm(FormType.CampaignAmount);
    setFormType(FormType.CampaignAmount);
  };

  const handleCancelClick = () => {
    const currentContextPrefixes = ["createCampaign", "updateCampaign"];
    setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM); // baseline
    setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    // Loop through all keys in localStorage and remove keys that match our context prefix types
    // for (let i = 0; i < localStorage.length; i++) {
    //   const key = localStorage.key(i);
    //   if (currentContextPrefixes.some(currentContextType => key.startsWith(`${currentContextType}_`))) {
    //     localStorage.removeItem(key);
    //   }
    // }
    // Loop through all keys in localStorage and remove keys that match our context types in more human readable form
    for (let key in localStorage) {
      currentContextPrefixes.forEach((prefix) => {
        if (key.startsWith(prefix)) {
          localStorage.removeItem(key);
        }
      });
    }
    // Specific key removal (if needed)
    localStorage.removeItem("subCatMenuData");
    setCurrentForm("createCampaign");
    setShowCreateModal(false);
  };

  const saveAdCategory = () => {
    setCurrentForm(FormType.CampaignAmount);
    setFormType(FormType.CampaignAmount);
  };
  const resetStateOnCampaignSuccess = () => {
    console.log("resetStateOnCampaignSuccess called in Parent!"); // Debugging
    const contextPrefixes = ["createCampaign", "updateCampaign"];

    for (let key in localStorage) {
      contextPrefixes.forEach((prefix) => {
        if (key.startsWith(prefix)) {
          console.log(`Clearing key: ${key}`); // Debugging
          localStorage.removeItem(key);
        }
      });
    }
    clearCookie("campaignId");
    localStorage.removeItem("subCatMenuData");
    setShowCreateModal(false); // Ensure this properly closes the modal
  };

  const cancelCategoryBackToParentCat = () => {
    localStorage.removeItem("subCatMenuData");
    setCurrentForm(FormType.CategoryNameFormatSearch);
    setFormType(FormType.CategoryNameFormatSearch);
  };

  const handleBackClick = () => {
    switch (currentForm) {
      case FormType.CategoryNameFormatSearch:
        setCurrentForm(FormType.CampaignAdModal);
        setFormType(FormType.CampaignAdModal);
        break;
      case FormType.RelationalCategoryMenuPopup:
        const selectedCategoryKey = getStorageKey(
          "selectedCategory",
          currentContextType
        );
        const storedCategory = localStorage.getItem(selectedCategoryKey);
        if (storedCategory) {
          const { categoryName, categoryId } = JSON.parse(storedCategory);
          handleCategoryClick(categoryName, categoryId);
        } else {
          setCurrentForm(FormType.CategoryNameFormatSearch);
          setFormType(FormType.CategoryNameFormatSearch);
        }
        break;
      case FormType.CampaignAmount:
        const selectedSubcategoryKey = getStorageKey(
          "selectedSubcategory",
          currentContextType
        );
        const storedSubcategory = localStorage.getItem(selectedSubcategoryKey);
        if (storedSubcategory) {
          const { subcategoryId, subcategoryName } =
            JSON.parse(storedSubcategory);
          handleSubcategoryClick(subcategoryId, subcategoryName);
        } else {
          const storedCategoryKey = getStorageKey(
            "selectedCategory",
            currentContextType
          );
          const storedCategoryData = localStorage.getItem(storedCategoryKey);
          if (storedCategoryData) {
            const { categoryName, categoryId } = JSON.parse(storedCategoryData);
            handleCategoryClick(categoryName, categoryId);
          }
        }
        setCurrentForm(FormType.RelationalCategoryMenuPopup);
        setFormType(FormType.RelationalCategoryMenuPopup);
        break;
      case FormType.Checkout:
        setCurrentForm(FormType.CampaignAmount);
        setFormType(FormType.CampaignAmount);
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    switch (currentForm) {
      case FormType.CampaignAdModal:
        setModalIcon(
          <DesktopComputerIcon
            width="10"
            height="10"
            fillColor="var(--color-black)"
          />
        );
        setModalTitle(
          currentContextType === "updateCampaign"
            ? "Edit Campaign"
            : "Create Campaign"
        );
        break;
      case FormType.CategoryNameFormatSearch:
        setModalIcon(
          <CatagoriesIcon
            width="10"
            height="10"
            fillColor="var(--color-black)"
          />
        );
        setModalTitle("Select Ad Category");
        break;
      case FormType.RelationalCategoryMenuPopup:
        setModalIcon(
          <CatagoriesIcon
            width="10"
            height="10"
            fillColor="var(--color-black)"
          />
        );
        setModalTitle("Select Ad Sub/Category");
        break;
      case FormType.CampaignAmount:
        setModalIcon(
          <ClipBoardCheckIcon
            width="10"
            height="10"
            fillColor="var(--color-black)"
          />
        );
        setModalTitle("Set Budget");
        break;
      case FormType.Checkout:
        setModalIcon(
          <CashRegisterIcon width="30" height="30" fillColor="#000" />
        );
        setModalTitle("Checkout");
        break;
      default:
        setModalTitle("Create New Ad");
        break;
    }
  }, [currentForm]);
  const handleStartDateChange = (startDate) => {
    const dateString = startDate ? startDate.toISOString() : "";
    const startDateKey = getStorageKey("startDateValue", currentContextType);
    setStartDateValue(startDate);
    localStorage.setItem(startDateKey, dateString);
  };
  const handleEndDateChange = (endDate) => {
    const dateString = endDate ? endDate.toISOString() : "";
    const endDateKey = getStorageKey("endDateValue", currentContextType);
    setEndDateValue(endDate); // Update state
    localStorage.setItem(endDateKey, dateString); // Update localStorage
  };
  const clearStartDate = () => {
    const startDateKey = getStorageKey("startDateValue", "eventsList");
    localStorage.removeItem(startDateKey);
    setStartDateValue("");
  };
  const clearEndDate = () => {
    const endDateKey = getStorageKey("endDateValue", "eventsList");
    localStorage.removeItem(endDateKey);
    setEndDateValue("");
  };
  const handleDateErrorChange = (hasError) => {
    console.log("Date order error status:", hasError); // This will log the error status
    setDateOrderError(hasError);
  };
  const toggleShowDates = () => {
    //created independent state active state since both can be active together
    setShowDates((prev) => !prev);
    // Clear local storage and update state when hiding the date pickers
    if (showDates) {
      const startDateKey = getStorageKey("startDateValue", currentContextType);
      localStorage.removeItem(startDateKey);
      const endDateKey = getStorageKey("endDateValue", currentContextType);
      localStorage.removeItem(endDateKey);
      // Assuming you have state variables like `startDateValue` and `endDateValue`
      setStartDateValue("");
      setEndDateValue("");
      // If additional handlers to notify changes, call them here
    }
  };

  const handleDeleteCampaign = useCallback(async (campaignId, sourceId) => {
    console.log("Deleting campaign with id:", campaignId);
    try {
      const response = await apiDeleteSourcesCamapainAndRelatedRecords({
        campaignId,
        sourceId,
      });
      console.log("Delete response:", response);
      // If deletion was successful, update state to remove the campaign.
      setCampaigns((prevCampaigns) =>
        prevCampaigns.filter((campaign) => campaign.id !== campaignId)
      );
    } catch (error) {
      console.error("Error deleting campaign:", error);
    }
  }, []);

  // AutoPay toggle handler (for example)
  const handleCampaignAutoPayToggle = useCallback(
    async (campaignId, newStatus) => {
      console.log("handleAutoPayToggle - campaigns:", campaigns);
      console.log("handleAutoPayToggle - campaignId:", campaignId);
      // Lookup the campaign from state.
      const campaign = campaigns.find((c) => c.id === campaignId);
      if (!campaign) {
        setErrorMessage("Campaign not found in state.");
        return;
      }
      if (campaign.campaignStatus === "suspended") {
        setErrorMessage(
          "This campaign is suspended. Please upload a campaign that meets our community guidelines. Too many suspensions may result in termination of your membership."
        );
        return;
      }
      // If the campaign is expired, show the error.
      if (campaign.campaignStatus === "expired") {
        setErrorMessage(autopayErrorMessages.expired);
        return;
      }
      try {
        await apiToggleCampaignAutoPay({
          campaignId,
          autoPaymentsEnabled: newStatus,
        });
        // On success, update the local state.
        setCampaigns((prev) =>
          prev.map((c) =>
            c.id === campaignId ? { ...c, autoPaymentsEnabled: newStatus } : c
          )
        );
        setErrorMessage(""); // Clear any error on success.
      } catch (err) {
        console.error("Error toggling autopay in backend:", err);
        setErrorMessage(autopayErrorMessages.server);
      }
    },
    [campaigns]
  );
  // Fetch campaigns on component mount (drop-in)
  const fetchingCampaigns = useCallback(
    async ({ reset = false, limit = 10 } = {}) => {
      if (!currentMemberId) return;
      setLoading(true);
      try {
        const cursor = reset ? null : lastStartedAt;
        const resp = await apiFetchCampaigns(currentMemberId, {
          lastStartedAt: cursor,
          limit,
        });
        // eject if suspended
        if (isSuspendedFrom(resp) || isSuspendedFrom(resp?.data)) {
          await handleLogout?.();
          return;
        }
        // unread (body first, then header)
        const unreadFromBody = Number(
          resp?.data?.unreadNotificationCount ?? resp?.unreadNotificationCount
        );
        const unreadFromHeader = Number(
          resp?.headers?.["x-unread-notifications"]
        );
        const unread = Number.isFinite(unreadFromBody)
          ? unreadFromBody
          : Number.isFinite(unreadFromHeader)
            ? unreadFromHeader
            : null;
        if (unread != null) notificationsCountFetchedFromMiddleware(unread);
        // tolerate axios/body
        const body = resp?.data !== undefined ? resp.data : resp;
        // normalize page
        const page = Array.isArray(body?.campaigns)
          ? body.campaigns
          : Array.isArray(body)
            ? body
            : [];
        setCampaigns((prev) => (reset ? page : [...(prev || []), ...page]));
        // cursor + hasMore
        if (page.length > 0) {
          const last = page[page.length - 1];
          setStartedCreatedAt(last?.startedAt || last?.createdAt || null);
          setHasMore(page.length >= limit);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        // unread from error payload if present
        const errCount = Number(
          error?.response?.data?.unreadNotificationCount ??
            error?.unreadNotificationCount
        );
        if (Number.isFinite(errCount))
          notificationsCountFetchedFromMiddleware(errCount);

        console.error("Failed to fetch campaigns", {
          msg: error?.message,
          status: error?.response?.status,
        });
      } finally {
        setLoading(false);
      }
    },
    [
      currentMemberId,
      lastStartedAt,
      notificationsCountFetchedFromMiddleware,
      handleLogout,
    ]
  );

  // Load more campaigns
  const fetchMoreCampaigns = () => {
    fetchingCampaigns();
  };

  // Initial load on mount or when `currentMemberId` changes
  useEffect(() => {
    if (currentMemberId) {
      fetchingCampaigns();
    }
  }, [currentMemberId]);

  const filteredCampaigns = campaigns.filter((campaign) => {
    const matchesName = campaign.campaignName
      .toLowerCase()
      .includes(debouncedSearchTerm.toLowerCase());
    const campaignStartDate = new Date(campaign.startedAt);
    const campaignEndDate = campaign.endDate
      ? new Date(campaign.endDate)
      : null;

    const withinDateRange =
      (!dateRange.start || campaignStartDate >= new Date(dateRange.start)) &&
      (!dateRange.end ||
        (campaignEndDate && campaignEndDate <= new Date(dateRange.end)));

    return matchesName && withinDateRange;
  });
  useEffect(() => {
    setDateRange({ start: startDateValue, end: endDateValue });
  }, [startDateValue, endDateValue]);

  // Edit flow
  const onCreatCampaignClick = () => {
    setVerifyNeeded(true); // <-- just flips the boolean on
    setCurrentContextType(FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED);
  };

  const createCampaignPermissionGranted = useCallback(() => {
    setShowCreateModal(true);
    setCurrentForm(FormType.CampaignAdModal);
    setFormType(FormType.CampaignAdModal);
    setCurrentContextType("createCampaign"); // switch to campaign flow
  }, [setShowCreateModal, setCurrentForm, setFormType, setCurrentContextType]);

  const onVerified = useCallback(() => {
    setVerifyNeeded(false); // hide TV
    // baseline the auth machine so OTP doesn't linger
    setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    // now proceed with the create-campaign flow
    createCampaignPermissionGranted();
  }, [
    setVerifyNeeded,
    setCurrentContextType,
    setCurrentFormStep,
    createCampaignPermissionGranted,
  ]);

  const handleEditCampaign = (campaign) => {
    setCurrentContextType("updateCampaign");
    setErrorMessage("");
    if (campaign.campaignStatus === "suspended") {
      setErrorMessage(
        "This campaign is suspended. Please upload a campaign that meets our community guidelines. Too many suspensions may result in termination of your membership."
      );
      return;
    }
    setCampaign(campaign); // Here, 'campaign' is an object that includes its id (e.g., campaign.id)
    // Store the campaign ID in a cookie (using your helper)
    setCookie("updateCampaign_campaignId", campaign.id);
    setCurrentForm(FormType.CampaignAdModal);
    setFormType(FormType.CampaignAdModal);
    setShowCreateModal(true);
  };

  return (
    <div id="adsManagerTop" className="adsManagerDashboardContainer">
      <SolidColorButton
        solidColorBtnImage={
          <LeftArrow
            height="15px"
            width="15px"
            fillColor="#964b00"
            strokeWidth="4"
            strokeColor="#964b00"
          />
        }
        className="gold-btn"
        solidColorBtnText="Back"
        solidColorBtnFontWeight="800"
        solidColorBtnFontFamily="helvetica"
        solidColorBtnTextFontSize=".75rem"
        solidColorBtnFontColor="#964b00"
        solidColorBtnWidth="100px"
        solidColorBtnBoxShadow="0 4px 3px 1px #000000bb, 0 6px 8px #000000bb, 0 -4px 4px #000000bb, 0 -6px 4px #000000bb, inset 0 0 3px 0 #000000bb"
        onClick={() => navigate(-1)}
      />
      <PointsDashBoard
        pointsBalance={currentMemberData?.pointsBalance || 0} // Use dynamic points balance from currentMemberData
        setPointsBalance={(newBalance) =>
          setCurrentMemberData({
            ...currentMemberData,
            pointsBalance: newBalance,
          })
        } // Update points balance logic
        userId={currentMemberId}
      />
      {/* Additional content for the dashboard */}
      {/* <PointsDashBoard
        pointsBalance={14010} // Pass current points balance (mock value for now)
        setPointsBalance={(newBalance) => console.log(newBalance)} // Update handler (replace with actual logic)
      /> // this is frontend testing just uncomment and change the points to test*/}
      {verifyNeeded && (
        <Television
          shouldTvText={false}
          onCancelled={() => {
            setVerifyNeeded(false); // hide the tv
            setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM); // baseline
            setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
          }}
          onVerified={onVerified} // optional: hide after success
        />
      )}
      {showCreateModal && !verifyNeeded && (
        <ModalControlRouterWrapper
          overlayClassName={
            currentForm === FormType.Checkout ? "checkout-modal" : ""
          }
          wrapperClassName={
            currentForm === FormType.Checkout ? "checkout-modal" : ""
          }
          title={modalTitle}
          icon={modalIcon}
          // whole expression will be true only if currentForm is not equal to
          // FormType.CampaignAdModal and currentForm is not equal to FormType.RelationalCategoryMenuPopup.
          // Therefore, when currentForm is equal to the other form types, then the back button will display
          onBack={
            currentForm !== FormType.CampaignAdModal &&
            currentForm !== FormType.RelationalCategoryMenuPopup
              ? handleBackClick
              : null
          }
          onCancel={handleCancelClick}
        >
          <CampaignStateManagerFlowForms
            currentForm={currentForm}
            setCurrentForm={setCurrentForm}
            formType={formType}
            setFormType={setFormType}
            updateCampaignAdModal={handleSaveAd}
            categories={categories}
            campaign={campaign}
            subCatMenuData={subCatMenuData}
            updateCategoryNameFormatSearch={handleCategoryClick}
            updateCampaignAmount={saveSetCampaignAmount}
            cancelCategoryBackToParentCat={cancelCategoryBackToParentCat}
            onResetStateOnCampaignSuccess={resetStateOnCampaignSuccess}
            currentContextType={currentContextType}
            setCurrentContextType={setCurrentContextType}
            saveAdCategory={saveAdCategory}
            handleSubcategoryClick={handleSubcategoryClick} // Pass this instead
            handlePromoteAllClick={handlePromoteAllClick}
            setErrorMessage={setErrorMessage} // Pass setErrorMessage here
          />
        </ModalControlRouterWrapper>
      )}
      {errorMessage && (
        <ErrorMessageModal
          message={errorMessage}
          onClose={() => setErrorMessage("")} // Clear the error
        />
      )}
      <div className="controls">
        <GlassBtn
          glassBtnImage={
            <BroadCastTowerIcon
              height="15px"
              width="15px"
              fillColor="var(--color-yellow)"
            />
          }
          onClick={onCreatCampaignClick}
          glassHoverStyle={{ height: "58px" }}
          className="glass-btn"
          glassRefClassName="createAdGlassBtnRef"
          glassBtnText="Create Campaign"
          glassBtnWidth="200px"
          glassBtnTextFontSize="1rem"
          glassBtnFontWeight="900"
          glassBtnHeight="30px"
          glassBtnFontColor="var(--color-yellow)"
          glassBtnTextDecorationColor="var(--color-yellow)"
          hi2Style={{
            transform: "rotate(90deg) translate(-40px, 100px)",
          }}
          hi3Style={{
            transform: "rotate(90deg) translate(-40px, -100px)",
          }}
        />
        <div className="campaign-search-wrapper blinker-wrapper">
          <input
            id="campaignSearch"
            type="text"
            placeholder="Enter Campaign name and press enter to search..."
            value={searchTermValue}
            onChange={searchValuesCollected}
            onFocus={() => setCampaignSearchFocused(true)}
            onBlur={() => setCampaignSearchFocused(false)}
            className={isCampaignSearchFocused ? "focused" : ""}
          />
          {!(isCampaignSearchFocused || searchTermValue) && (
            <>
              <MagnifyingGlassIcon
                fillColor="var(--color-yellow)"
                width="24px"
                height="24px"
                className="search-icon"
              />
              <span className="blinking-cursor-gold-color campaign-cursor">
                |
              </span>
            </>
          )}
        </div>
        {!showDates && (
          <ImgTopBtn
            image={
              <CalendarIcon
                height="20px"
                width="20px"
                style={{ paddingRight: "5px" }}
              />
            }
            onClick={() => setShowDates(true)}
            imgTopBtnText="Press to Filter by Date Range"
            imgTopBtnWidth="250px"
            padding-right=" 5px"
            padding-left="5px"
            imgTopBtnFontColor="var(--color-bloodred)"
            position="relative"
          />
        )}
        {showDates && ( // if show dates is not false then show this
          <div id="campaignDashboard" className="date-picker-section">
            <DatePicker
              onClick={() => setDatePickerOpen(true)}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
              clearStartDate={clearStartDate}
              clearEndDate={clearEndDate}
              setStartDateValue={setStartDateValue}
              setEndDateValue={setEndDateValue}
              startDateValue={startDateValue}
              endDateValue={endDateValue}
              onDateErrorChange={handleDateErrorChange}
              showAdditionalDate={false}
              isClearable={true}
              showYearDropdown={true} // show past years
              showMonthDropdown={true}
              numYears={10} // Show the last 10 years
              dateFormat="MMMM d, yyyy Date"
              placeholder="Enter/Select  Date"
            />
            <GlassBtn
              glassBtnImage={
                <XIcon
                  height="10px"
                  width="10px"
                  fillColor="var(--color-darkGold)"
                />
              }
              onClick={toggleShowDates}
              reverseOrder={true}
              glassBtnText="Cancel Date Search"
              glassBtnFontColor="var(--color-darkGold)"
              glassBtnWidth="130px"
              glassBtnFontWeight="800"
              glassBtnFontFamily="roboto-slab"
              glassBtnHeight="30px"
              glassBtnTextFontSize=".75rem"
              textLocalStyle={{
                position: "absolute",
                bottom: "15%",
              }}
              hi2Style={{
                transform: "rotate(90deg) translate(-70px, 65px)",
              }}
              hi3Style={{
                transform: "rotate(90deg) translate(-70px, -65px)",
              }}
              glassHoverStyle={{ height: "70px" }}
            />
          </div>
        )}
      </div>
      {filteredCampaigns.length > 0 ? (
        <div className="campaign-container">
          {filteredCampaigns.map((campaign) => (
            <CampaignMetricsCard
              key={campaign.id}
              campaign={campaign}
              onEdit={handleEditCampaign}
              onDelete={handleDeleteCampaign}
              onAutoPayToggle={handleCampaignAutoPayToggle}
            />
          ))}
          {loading && <Loader />}
          {hasMore && (
            <SmallPlainInsetBtn
              image={
                hasMore ? (
                  <EyeIcon
                    height="15px"
                    width="15px"
                    style={{ marginRight: "3px" }}
                    fillColor="var(--color-bloodred)"
                  />
                ) : (
                  <EyeSlashIcon
                    height="15px"
                    width="15px"
                    style={{ marginRight: "3px" }}
                    fillColor="var(--color-bloodred)"
                  />
                )
              }
              className="load-more-button"
              text={hasMore ? "View More" : "Hide More"}
              onClick={fetchMoreCampaigns}
            />
          )}
        </div>
      ) : (
        !loading && <p>No campaigns found.</p>
      )}
    </div>
  );
};
export default CurrentUserAdsManagerDashboard;
